import React, { ReactNode } from 'react'

import UpSeatLogo from '@/assets/logos/upseat_logo.svg'
import { ClientLockup } from '@/components/upseat-ui/login/ClientLockup'
import { useClientBranding } from '@/hooks/app/useClientBranding'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'

type PublicLayoutProps = {
  children: ReactNode
  /**
   * Where the Up / client lockup sits.
   *
   * `top` (default) tucks it out of the way for the content-heavy pages — extra
   * details, password setup, team login — whose forms are pinned to the bottom and
   * can grow tall enough to reach it. `half` centres it in the upper half of the
   * screen, pairing with a form centred in the lower half; used where the identity
   * is the point of the screen rather than a header, i.e. sign-in.
   */
  headerPlacement?: 'top' | 'half'
  /**
   * Opt a page out of client branding entirely.
   *
   * The attribution is sticky for the session, so without this an Up staff member
   * who followed a client's link earlier would find the internal team sign-in
   * showing that client's logo. Whitelabelling is for customer-facing screens;
   * anything internal sets this false.
   */
  whitelabel?: boolean
}

const PublicLayout: React.FC<PublicLayoutProps> = ({
  children,
  headerPlacement = 'top',
  whitelabel = true,
}) => {
  const navigate = useNavigate()
  // Whitelabel: when the visitor arrived from a client's outreach we show that
  // client's mark alongside our own. The page keeps Up's palette either way —
  // only the identity changes, not the surface it sits on.
  const resolved = useClientBranding()
  const name = whitelabel ? resolved.name : null
  const logoUrl = whitelabel ? (resolved.branding?.logo_url ?? null) : null

  return (
    <div className="app-view app-column bg-purple overflow-hidden">
      <div
        className={
          headerPlacement === 'half'
            ? 'absolute top-0 left-0 w-full h-[50svh] flex justify-center items-center px-6'
            : 'absolute left-0 w-full flex justify-center items-center px-6 py-14'
        }
      >
        {name ? (
          <ClientLockup name={name} logoUrl={logoUrl} />
        ) : (
          <button
            type="button"
            onClick={() => navigate('/')}
            className="flex cursor-pointer"
          >
            <motion.img
              src={UpSeatLogo}
              alt="up-logo"
              className={`w-[10vw] desktop:w-[44px]`}
              initial={{ scale: 0.5 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{
                type: 'spring',
                stiffness: 260,
                damping: 20,
              }}
            />
          </button>
        )}
      </div>
      {children}
    </div>
  )
}

export default PublicLayout
